import { PageName, PageSection, PageType } from '@/shared/types/segment';

/**
 * Maps every `PageName` to the taxonomy sent on its page view. Exhaustive, so a
 * new page fails the build until it is mapped. Keep values stable — changing one
 * breaks historical Amplitude aggregation.
 *
 * `path` pins the template for a page whose pathname is bot-controlled, so the
 * URL never reaches `path_template` (and no `resource_id` is derived from it).
 */
export const PAGE_TAXONOMY: Record<
  PageName,
  { section: PageSection; type: PageType; path?: string }
> = {
  [PageName.ACCEPT_INVITATION]: { section: 'auth', type: 'auth' },
  [PageName.ACCOUNT_SETTINGS]: { section: 'settings', type: 'settings' },
  [PageName.AI_AGENTS]: { section: 'ai-agents', type: 'list' },
  [PageName.AI_AGENT_GUIDELINES]: { section: 'ai-agents', type: 'editor' },
  [PageName.AI_AGENT_OVERVIEW]: { section: 'ai-agents', type: 'detail' },
  [PageName.AI_AGENT_SETTINGS]: { section: 'ai-agents', type: 'settings' },
  [PageName.API_KEYS]: { section: 'settings', type: 'settings' },
  [PageName.ANALYTICS_OVERVIEW]: { section: 'analytics', type: 'analytics' },
  [PageName.ANALYTICS_USAGE]: { section: 'analytics', type: 'analytics' },
  [PageName.ANALYTICS_BRAIN_EFFECTIVENESS]: { section: 'analytics', type: 'analytics' },
  [PageName.ANALYTICS_HUMAN_EFECTIVENESS]: { section: 'analytics', type: 'analytics' },
  [PageName.ANALYTICS_HUMAN_AGENTS]: { section: 'analytics', type: 'analytics' },
  [PageName.ANALYTICS_MY_PERFORMANCE]: { section: 'analytics', type: 'analytics' },
  [PageName.ANALYTICS_AI_AGENTS]: { section: 'analytics', type: 'analytics' },
  [PageName.ANALYTICS_AGENTS_PERFORMANCE]: { section: 'analytics', type: 'analytics' },
  [PageName.ANALYTICS_ACCOUNT_CHAT_LOGS]: { section: 'analytics', type: 'list' },
  [PageName.ANALYTICS_CUSTOMER_SATISFACTION]: { section: 'analytics', type: 'analytics' },
  [PageName.ANALYTICS_CUSTOM_REPORTS]: { section: 'analytics', type: 'list' },
  [PageName.ANALYTICS_CUSTOM_REPORT_DETAIL]: { section: 'analytics', type: 'analytics' },
  [PageName.ANALYTICS_USER_FEEDBACK]: { section: 'analytics', type: 'analytics' },
  [PageName.BILLING]: { section: 'billing', type: 'settings' },
  [PageName.BRAIN_LOGS]: { section: 'ai-agents', type: 'list' },
  [PageName.BROADCAST]: { section: 'broadcasts', type: 'detail' },
  [PageName.BROADCASTS]: { section: 'broadcasts', type: 'list' },
  [PageName.BUNDLE]: { section: 'ai-agents', type: 'detail' },
  [PageName.BUNDLES]: { section: 'ai-agents', type: 'list' },
  [PageName.BUSINESS_HOUR]: { section: 'settings', type: 'editor' },
  [PageName.BUSINESS_HOURS]: { section: 'settings', type: 'settings' },
  [PageName.CHATS]: { section: 'chats', type: 'list' },
  [PageName.COLLECTION]: { section: 'knowledge-bases', type: 'detail' },
  [PageName.COLLECTIONS]: { section: 'knowledge-bases', type: 'list' },
  [PageName.CONTACT]: { section: 'contacts', type: 'detail', path: '/:slug/contacts/:id' },
  [PageName.CONTACTS]: { section: 'contacts', type: 'list' },
  [PageName.CONVERSATION]: { section: 'chats', type: 'detail' },
  [PageName.CONVERSATIONS]: { section: 'chats', type: 'list' },
  [PageName.CREATE_ACCOUNT]: { section: 'onboarding', type: 'wizard' },
  [PageName.DATASOURCE]: { section: 'knowledge-bases', type: 'detail' },
  [PageName.DEPARTMENTS]: { section: 'settings', type: 'list' },
  [PageName.DATA_PROCESS]: { section: 'settings', type: 'settings' },
  [PageName.DEBTS]: { section: 'contacts', type: 'list' },
  [PageName.DESK_SETTINGS]: { section: 'settings', type: 'settings' },
  [PageName.DIALOG]: { section: 'ai-agents', type: 'editor' },
  [PageName.DIALOGS]: { section: 'ai-agents', type: 'list' },
  [PageName.ENTITIES]: { section: 'ai-agents', type: 'list' },
  [PageName.ENTITY]: { section: 'ai-agents', type: 'editor' },
  [PageName.ERROR_500]: { section: 'system', type: 'other', path: '/error' },
  [PageName.HOME]: { section: 'home', type: 'other' },
  [PageName.INSIGHTS]: { section: 'ai-agents', type: 'list' },
  [PageName.INTEGRATION]: { section: 'integrations', type: 'detail' },
  [PageName.INTEGRATIONS]: { section: 'integrations', type: 'list' },
  [PageName.INTENT]: { section: 'ai-agents', type: 'editor' },
  [PageName.INTENTS]: { section: 'ai-agents', type: 'list' },
  [PageName.INVOICES]: { section: 'billing', type: 'list' },
  [PageName.LANGUAGE_MODELS]: { section: 'settings', type: 'settings' },
  [PageName.MCPS]: { section: 'ai-agents', type: 'list' },
  [PageName.MCP_SERVER]: { section: 'ai-agents', type: 'detail' },
  [PageName.MANAGE_MEMBER_ACCESS]: { section: 'settings', type: 'list' },
  [PageName.MANAGE_ROLE_ACCESS]: { section: 'settings', type: 'list' },
  [PageName.MANAGE_ROLE_PERMISSIONS]: { section: 'settings', type: 'detail' },
  [PageName.MANAGE_TEAM_ACCESS]: { section: 'settings', type: 'list' },
  [PageName.NOT_FOUND]: { section: 'system', type: 'other', path: '/not-found' },
  [PageName.ONBOARDING_0]: { section: 'onboarding', type: 'wizard' },
  [PageName.ONBOARDING_1]: { section: 'onboarding', type: 'wizard' },
  [PageName.ONBOARDING_2]: { section: 'onboarding', type: 'wizard' },
  [PageName.ONBOARDING_3]: { section: 'onboarding', type: 'wizard' },
  [PageName.PERSONAL_INVITATIONS]: { section: 'settings', type: 'settings' },
  [PageName.PERSONAL_NOTIFICATIONS]: { section: 'settings', type: 'settings' },
  [PageName.PERSONAL_PROFILE]: { section: 'settings', type: 'settings' },
  [PageName.PERSONAL_SECURITY]: { section: 'settings', type: 'settings' },
  [PageName.RULE]: { section: 'settings', type: 'editor' },
  [PageName.RULES]: { section: 'settings', type: 'list' },
  [PageName.UNAUTHORIZED]: { section: 'auth', type: 'auth' },
  [PageName.USAGE]: { section: 'billing', type: 'list' },
  [PageName.VERSIONS]: { section: 'ai-agents', type: 'list' },
  [PageName.WEBHOOK]: { section: 'ai-agents', type: 'editor' },
  [PageName.WEBHOOKS]: { section: 'ai-agents', type: 'list' },
  [PageName.WELCOME]: { section: 'auth', type: 'other' },

  // Campaigns
  [PageName.CAMPAIGNS]: { section: 'campaigns', type: 'list' },
  [PageName.CAMPAIGN_SETUP]: { section: 'campaigns', type: 'wizard' },
  [PageName.CAMPAIGN_MESSAGING]: { section: 'campaigns', type: 'wizard' },
  [PageName.CAMPAIGN_REVIEW]: { section: 'campaigns', type: 'wizard' },
  [PageName.CAMPAIGN_LOGS]: { section: 'campaigns', type: 'list' },
  [PageName.CAMPAIGN_ANALYTICS]: { section: 'campaigns', type: 'analytics' },
  [PageName.CAMPAIGN_PERFORMANCE]: { section: 'campaigns', type: 'analytics' },
  [PageName.CAMPAIGN_SUBSCRIBER_JOURNEY]: { section: 'campaigns', type: 'detail' },

  // Simulations
  [PageName.SIMULATIONS]: { section: 'simulations', type: 'list' },
  [PageName.SIMULATIONS_OVERVIEW]: { section: 'simulations', type: 'analytics' },
  [PageName.SIMULATION_RUN]: { section: 'simulations', type: 'detail' },

  // Improvements
  [PageName.IMPROVEMENTS]: { section: 'improvements', type: 'list' },
};
